import React, { useState, useCallback, useEffect } from 'react';
import { ParametersLayout } from './ParametersLayout';
import { ui } from '../../../config/uiSizes';
import { useFetchDivisions } from '../../../hooks/api';
import { useAuth } from '../../../hooks/auth';

interface DivisionMappingRow {
  id: string;
  sourceDivision: string;
  targetDivision: string;
  [key: string]: unknown;
}

interface DivisionMappingParametersProps {
  parameters?: Record<string, unknown>;
  onParametersChange?: (parameters: Record<string, unknown>) => void;
  /** True when this transformation belongs to a D2D migration */
  isD2D?: boolean;
  /** Source org key (shortName preferred, falls back to org name) */
  sourceOrgKey?: string;
  /** Target org key (shortName preferred, falls back to org name) */
  targetOrgKey?: string;
  /** Pre-populated source division name (D2D only) */
  sourceDivisionName?: string;
  /** Pre-populated target division name (D2D only) */
  targetDivisionName?: string;
}

// ── D2D read-only summary ─────────────────────────────────────────────────────

const D2DReadOnlySummary: React.FC<{
  sourceDivisionName: string;
  targetDivisionName: string;
}> = ({ sourceDivisionName, targetDivisionName }) => (
  <div className={`space-y-4`}>
    <div className={`rounded-lg border border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 ${ui.page.cardPadding}`}>
      <p className={`${ui.text.label} font-semibold text-blue-800 dark:text-blue-300 mb-3`}>
        Division mapping is pre-configured for this Division-to-Division migration.
      </p>
      <div className={`flex items-center ${ui.gap.md}`}>
        <div className="flex-1 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2">
          <p className={`${ui.text.xs} text-gray-500 dark:text-gray-400 mb-0.5`}>Source Division</p>
          <p className={`${ui.text.body} font-medium text-gray-900 dark:text-gray-100`}>
            {sourceDivisionName || '—'}
          </p>
        </div>
        <svg className={`${ui.svg.sm} text-gray-400 shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
        <div className="flex-1 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2">
          <p className={`${ui.text.xs} text-gray-500 dark:text-gray-400 mb-0.5`}>Target Division</p>
          <p className={`${ui.text.body} font-medium text-gray-900 dark:text-gray-100`}>
            {targetDivisionName || '—'}
          </p>
        </div>
      </div>
    </div>
  </div>
);

// ── Division dropdown (reusable inside this file) ─────────────────────────────

const DivisionDropdown: React.FC<{
  label: string;
  value: string;
  orgId: string;
  onChange: (id: string) => void;
  disabledId?: string;
}> = ({ label, value, orgId, onChange, disabledId }) => {
  const { divisions, loading, fetchDivisions } = useFetchDivisions(orgId);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (orgId) void fetchDivisions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        listRef.current && !listRef.current.contains(t) &&
        btnRef.current && !btnRef.current.contains(t)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = search.trim()
    ? divisions.filter((d) => d.name.toLowerCase().includes(search.toLowerCase()))
    : divisions;

  const selected = divisions.find((d) => d.id === value);

  return (
    <div className="flex-1 relative">
      <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-1`}>
        {label}
      </label>
      <button
        type="button"
        ref={btnRef}
        onClick={() => !loading && setOpen((v) => !v)}
        disabled={loading || !orgId}
        className={`w-full text-left ${ui.text.body} flex items-center justify-between px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`truncate ${!selected ? 'text-gray-400 dark:text-gray-500' : ''}`}>
          {loading ? 'Loading divisions...' : selected?.name ?? 'Select a division'}
        </span>
        <svg className={`${ui.svg.sm} text-gray-400 shrink-0 animate-${loading ? 'spin' : 'none'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div ref={listRef} className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-52 overflow-y-auto" role="listbox">
          <div className="p-2 border-b border-gray-100 dark:border-gray-700 relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search divisions..."
              className={`w-full ${ui.input.sm} border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 ${search ? 'pr-7' : ''}`}
              autoFocus
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                aria-label="Clear search"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {filtered.length === 0 ? (
            <div className={`px-3 py-2 ${ui.text.xs} text-gray-500 dark:text-gray-400`}>No divisions found</div>
          ) : (
            filtered.map((d) => {
              const disabled = d.id === disabledId;
              return (
                <button
                  key={d.id}
                  type="button"
                  role="option"
                  aria-selected={value === d.id}
                  disabled={disabled}
                  onClick={() => { onChange(d.id); setOpen(false); setSearch(''); }}
                  className={`w-full text-left px-3 py-2 ${ui.text.body} transition-colors ${disabled
                    ? 'opacity-40 cursor-not-allowed text-gray-400 dark:text-gray-600'
                    : value === d.id
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100'
                    }`}
                >
                  {d.name}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

export const DivisionMappingParameters: React.FC<DivisionMappingParametersProps> = ({
  parameters,
  onParametersChange,
  isD2D = false,
  sourceOrgKey = '',
  targetOrgKey = '',
  sourceDivisionName = '',
  targetDivisionName = ''
}) => {
  const { userInfo } = useAuth();
  const fallbackOrgKey = userInfo?.orgName ?? '';
  const sourceOrgIdForLookup = sourceOrgKey || fallbackOrgKey;
  const targetOrgIdForLookup = targetOrgKey || fallbackOrgKey;

  const [rows, setRows] = useState<DivisionMappingRow[]>(() => {
    const divisionMap = parameters?.['divisionMap'];

    if (divisionMap && typeof divisionMap === 'object') {
      if (Array.isArray(divisionMap)) {
        return divisionMap.length > 0
          ? divisionMap
          : [{ id: '1', sourceDivision: '', targetDivision: '' }];
      } else {
        const entries = Object.entries(divisionMap as Record<string, string>);
        if (entries.length > 0) {
          return entries.map(([sourceDivision, targetDivision], index) => ({
            id: (index + 1).toString(),
            sourceDivision,
            targetDivision
          }));
        }
      }
    }

    return [{ id: '1', sourceDivision: '', targetDivision: '' }];
  });

  const [defaultDivision, setDefaultDivision] = useState<string>(
    (parameters?.['defaultDivision'] as string) || ''
  );
  const [updateReferences, setUpdateReferences] = useState<boolean>(
    parameters?.['updateReferences'] !== undefined
      ? (parameters['updateReferences'] as boolean)
      : true
  );
  const [advancedOpen, setAdvancedOpen] = useState(false);

  useEffect(() => {
    if (onParametersChange) {
      const divisionMapObject = rows.reduce<Record<string, string>>((acc, row) => {
        if (row.sourceDivision && row.targetDivision) {
          acc[row.sourceDivision] = row.targetDivision;
        }
        return acc;
      }, {});

      const params: Record<string, unknown> = {
        divisionMap: divisionMapObject,
        updateReferences
      };

      if (defaultDivision) {
        params['defaultDivision'] = defaultDivision;
      }

      onParametersChange(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, defaultDivision, updateReferences]);

  const handleAddRow = useCallback(() => {
    setRows((prev) => [...prev, { id: Date.now().toString(), sourceDivision: '', targetDivision: '' }]);
  }, []);

  const handleRemoveRow = useCallback((id: string | number) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  }, []);

  const handleUpdateRow = useCallback(
    (id: string | number, field: 'sourceDivision' | 'targetDivision', value: string) => {
      setRows((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
    },
    []
  );

  // ── D2D read-only view ───────────────────────────────────────────────────────
  if (isD2D) {
    return (
      <D2DReadOnlySummary
        sourceDivisionName={sourceDivisionName}
        targetDivisionName={targetDivisionName}
      />
    );
  }

  // ── Standard editable view (Org-to-Org) ─────────────────────────────────────
  const renderRow = (row: DivisionMappingRow, index: number, onRemove: () => void) => (
    <div className={`flex items-end ${ui.gap.md}`}>
      <DivisionDropdown
        label={index === 0 ? 'Source Division' : ''}
        value={row.sourceDivision}
        orgId={sourceOrgIdForLookup}
        onChange={(id) => handleUpdateRow(row.id, 'sourceDivision', id)}
        disabledId={row.targetDivision}
      />

      <div className="flex items-center pb-2">
        <svg className={`${ui.svg.sm} text-gray-400 dark:text-gray-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
      </div>

      <DivisionDropdown
        label={index === 0 ? 'Target Division' : ''}
        value={row.targetDivision}
        orgId={targetOrgIdForLookup}
        onChange={(id) => handleUpdateRow(row.id, 'targetDivision', id)}
        disabledId={row.sourceDivision}
      />

      <button
        type="button"
        onClick={onRemove}
        className={`flex items-center gap-2 ${ui.input.base} ${ui.text.label} text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors mb-0.5`}
      >
        <svg className={ui.svg.sm} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
        Remove
      </button>
    </div>
  );

  return (
    <div className="space-y-4">
      <ParametersLayout<DivisionMappingRow>
        title="Division Mapping"
        description="Map divisions from source organization to target organization."
        addButtonLabel="Add division mapping"
        rows={rows}
        onAddRow={handleAddRow}
        onRemoveRow={handleRemoveRow}
        renderRow={renderRow}
      />

      {/* Update References checkbox */}
      <div className={`border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 ${ui.page.cardPadding}`}>
        <div className={`flex items-center ${ui.gap.sm}`}>
          <input
            type="checkbox"
            id="updateReferences"
            checked={updateReferences}
            onChange={(e) => setUpdateReferences(e.target.checked)}
            className={`${ui.svg.sm} text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-400`}
          />
          <label htmlFor="updateReferences" className={`${ui.text.label} text-gray-700 dark:text-gray-300`}>
            Update division references in properties
          </label>
        </div>
      </div>

      {/* Advanced options (collapsed by default) */}
      <div className="border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 overflow-hidden">
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          className={`w-full flex items-center justify-between ${ui.page.cardPadding} text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors`}
        >
          <span className={`${ui.text.label} font-medium text-gray-700 dark:text-gray-300`}>
            Advanced options
          </span>
          <svg
            className={`${ui.svg.sm} text-gray-400 transition-transform ${advancedOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {advancedOpen && (
          <div className={`border-t border-gray-200 dark:border-gray-600 ${ui.page.cardPadding}`}>
            <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
              Default Division (Optional)
            </label>
            <input
              type="text"
              value={defaultDivision}
              onChange={(e) => setDefaultDivision(e.target.value)}
              placeholder="default-target-division-id"
              className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            <p className={`${ui.text.xs} text-gray-500 dark:text-gray-400 mt-1`}>
              Division ID to use as a fallback when no mapping matches.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DivisionMappingParameters;

